import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { A2uiPayload } from '../../components/GritClaw/a2ui/types';
import type { ConfirmDecision } from '../../components/GritClaw/a2ui/A2uiBlock';
import {
  deriveProactive,
  type Proactive,
} from '../../components/GritClaw/a2ui/incident';
import {
  streamRun,
  wakeOnOpen,
  fetchConversations,
  fetchHistory,
  fetchInbox,
  uploadAttachment,
  createGritClawConversationId,
  getStoredGritClawConversationId,
  setStoredGritClawConversationId,
  normalizeGritClawConversationId,
  resetGritClawThreadId,
  updateConversation,
  deleteConversation,
  deleteAllConversations,
  cancelGritClawRun,
  createGritClawRunId,
  fetchDiagnosticsCapabilities,
} from '../../services/Api/gritClawClient';
import type {
  AgUiHandlers,
  GritClawConversation,
  HistoryMessage,
  PendingItem,
} from '../../services/Api/gritClawClient';
import {
  MAX_ATTACHMENTS_PER_MESSAGE,
  validateAttachmentFile,
  type AttachmentRejectReason,
} from './attachments';
import { useSnackbar } from '../../utils/useSnackbar';
import SnackbarComponent from '../../components/Snackbar/SnackbarComponent';
import {
  GRITCLAW_INSET_VAR,
  GRITCLAW_PANEL_WIDTH,
  MOTION,
} from '../../theme/tokens';
import {
  deriveScreenContext,
  type ScreenContext,
  type ScreenOverride,
} from './screenContext';
import { type AskPromptId, buildAskGritClawSeed } from './askGritClawSeed';
import { mergeWake } from './inbox';

/** Lightweight snapshot of a file the user attached to a turn, shown as read-only
 *  chips under their sent bubble. History reload does not carry these back — the
 *  gateway doesn't echo attachments in /app/history, and we don't fabricate them. */
export interface UserAttachmentMeta {
  name: string;
  size: number;
  type: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  uiBlocks: A2uiPayload[];
  activeTools: { id: string; name: string }[];
  streaming: boolean;
  progress?: { label: string; phase: string };
  corrId?: string;
  runId?: string;
  error?: string;
  /** Present only on freshly-sent user turns that carried file attachments. */
  attachments?: UserAttachmentMeta[];
}

/** A piece of attached context the user explicitly scopes a turn to (chips in
 *  the composer). Pushed into the screen override so turns resolve "these". */
export interface ContextRef {
  kind: 'device' | 'shipment' | 'alert' | 'location';
  id: string;
  label: string;
  meta?: string;
}

/** A local file staged on the composer for the next GritClaw turn. */
export interface FileRef {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  /** Set once this file has been uploaded successfully in-session (keyed by the
   *  FileRef identity). A retry after a sibling upload fails reuses this id
   *  instead of re-uploading — uploads expire server-side after 24h, so an
   *  in-session cached id is never stale. */
  uploadedId?: string;
}

interface GritClawCookieUser {
  id?: string;
  email?: string;
  company_id?: string;
}

interface GritClawCookies {
  token?: string;
  user?: GritClawCookieUser;
  super_user?: unknown;
  original_company_id?: string;
}

interface GritClawContextValue {
  messages: ChatMessage[];
  isStreaming: boolean;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  sendMessage: (text: string, displayText?: string) => boolean;
  stopTurn: () => void;
  retryMessage: (messageId: string) => void;
  confirmAction: (d: ConfirmDecision) => void;
  clearConversation: () => void;
  conversations: GritClawConversation[];
  activeConversationId: string;
  conversationListLoading: boolean;
  conversationListError: boolean;
  historyLoading: boolean;
  historyLoadError: boolean;
  selectConversation: (conversationId: string) => void;
  refreshConversations: () => void;
  renameConversation: (conversationId: string, title: string) => void;
  setConversationPinned: (conversationId: string, pinned: boolean) => void;
  deletingConversationIds: readonly string[];
  clearingConversationHistory: boolean;
  conversationMutationPending: boolean;
  conversationEpoch: number;
  conversationRevision: number;
  removeConversation: (
    conversationId: string,
    expectedEpoch: number,
    expectedRevision: number
  ) => Promise<boolean>;
  clearConversationHistory: (
    expectedEpoch: number,
    expectedRevision: number
  ) => Promise<boolean>;
  setScreenOverride: (o: ScreenOverride | null) => void;
  askGritClawAbout: (
    selection: Record<string, unknown>,
    promptId?: AskPromptId
  ) => void;
  detachScreenContext: () => void;
  currentOverride: ScreenOverride | null;
  currentScreenContext: () => ScreenContext;
  wakeIfNeeded: () => void;
  inboxCount: number;
  dismissInbox: () => void;
  // ── Attached context (composer chips, scoped per turn) ───────────────────
  attachedContext: ContextRef[];
  attachContext: (refs: ContextRef[]) => void;
  detachContext: (id: string) => void;
  clearContext: () => void;
  attachedFiles: FileRef[];
  attachFiles: (files: File[]) => void;
  detachFile: (id: string) => void;
  clearFiles: () => void;
  /** True while staged files are uploading on send — composer disables + shows it. */
  uploadingAttachments: boolean;
  // ── Docked bar expand / full-page handoff ────────────────────────────────
  barExpanded: boolean;
  setBarExpanded: (b: boolean) => void;
  requestFull: () => void;
  fullTransitioning: boolean;
  // ── Dismissed inline pins ────────────────────────────────────────────────
  dismissedPins: Set<string>;
  dismissPin: (id: string) => void;
  // ── Pending inbox items + derived proactive banner ───────────────────────
  pendingItems: PendingItem[];
  proactive: Proactive | null;
  // ── Bar focus (Cmd/Ctrl-K) ───────────────────────────────────────────────
  focusBarNonce: number;
  focusBar: () => void;
  diagnosticsEnabled: boolean;
  diagnosticsRawEnabled: boolean;
}

const GritClawCtx = createContext<GritClawContextValue | null>(null);

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const requestStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
};

// `created_at` is immutable for one server-side conversation lifecycle. Keep
// it separate from mutable list fields (title, pin, turn count, activity) so a
// normal refresh does not invalidate an in-flight attachment upload, while a
// deleted-and-recreated logical id does.
const UNKNOWN_CONVERSATION_LIFECYCLE_FINGERPRINT = 'created:<unknown>';
const conversationLifecycleFingerprint = (
  conversation: GritClawConversation
): string =>
  conversation.created_at
    ? `created:${conversation.created_at}`
    : UNKNOWN_CONVERSATION_LIFECYCLE_FINGERPRINT;

/** Map a persisted history payload into chat rows (pure; unit-tested). Assistant
 *  turns may carry persisted generative-UI blocks so cards survive a reload. */
export const historyToMessages = (history: HistoryMessage[]): ChatMessage[] =>
  history.map((h) => ({
    id: newId(),
    role: h.role,
    text: h.text,
    uiBlocks: h.ui_blocks ?? [],
    activeTools: [],
    streaming: false,
    ...(h.role === 'assistant' && h.corr_id ? { corrId: h.corr_id } : {}),
  }));

export const GritClawProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const translateRef = useRef(t);
  translateRef.current = t;
  const showInfoRef = useRef(snackbar.showInfo);
  showInfoRef.current = snackbar.showInfo;
  const showErrorRef = useRef(snackbar.showError);
  showErrorRef.current = snackbar.showError;
  const cookieResult = useCookies([
    'token',
    'user',
    'super_user',
    'original_company_id',
  ]) as unknown;
  const cookies =
    Array.isArray(cookieResult) && cookieResult[0]
      ? (cookieResult[0] as Partial<GritClawCookies>)
      : {};
  const typedCookies = cookies as GritClawCookies;
  const authToken =
    typeof typedCookies.token === 'string' ? typedCookies.token : '';
  const authSubject = String(
    typedCookies.user?.id || typedCookies.user?.email || ''
  );
  const authCompany = String(typedCookies.user?.company_id || '');
  // Impersonation sessions never talk to the GritClaw gateway: its membership
  // revalidation rejects them (no membership in the acted-for company) and
  // destructively unlinks the sender's schedules/push state in the process.
  const impersonating =
    (!!typedCookies.super_user && !!typedCookies.user?.company_id) ||
    !!typedCookies.original_company_id;
  // Access tokens rotate during one login, so the owner key deliberately
  // excludes the token. The monotonic epoch below changes on logout/login or an
  // owner switch, preventing A -> logout -> A ABA completions from touching the
  // fresh session while allowing an accepted pre-rotation request to finish.
  const authScopeKey =
    authToken && !impersonating && (authSubject || authCompany)
      ? `${authCompany}:${authSubject}`
      : '';
  const authEpochStateRef = useRef({ owner: authScopeKey, epoch: 1 });
  if (authEpochStateRef.current.owner !== authScopeKey) {
    authEpochStateRef.current = {
      owner: authScopeKey,
      epoch: authEpochStateRef.current.epoch + 1,
    };
  }
  const authEpoch = authEpochStateRef.current.epoch;
  const authEpochRef = useRef(authEpoch);
  authEpochRef.current = authEpoch;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef<ChatMessage[]>(messages);
  const [isStreaming, setIsStreaming] = useState(false);
  const isStreamingRef = useRef(isStreaming);
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState(false);
  const [diagnosticsRawEnabled, setDiagnosticsRawEnabled] = useState(false);
  const [panelOpen, setPanelOpenState] = useState(false);
  const [override, setOverride] = useState<ScreenOverride | null>(null);
  const [screenContextSuppressed, setScreenContextSuppressed] = useState(false);
  const wokeForThisOpenRef = useRef(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [attachedContext, setAttachedContext] = useState<ContextRef[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<FileRef[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const uploadingAttachmentsRef = useRef(uploadingAttachments);
  const uploadBatchSeqRef = useRef(0);
  const activeUploadRef = useRef<{ epoch: number; seq: number } | null>(null);
  const [barExpanded, setBarExpanded] = useState(false);
  const [fullTransitioning, setFullTransitioning] = useState(false);
  const [dismissedPins, setDismissedPins] = useState<Set<string>>(
    () => new Set()
  );
  const [focusBarNonce, setFocusBarNonce] = useState(0);
  const [activeConversationId, setActiveConversationIdState] = useState(
    getStoredGritClawConversationId
  );
  // Latest active conversation id, readable synchronously inside async resolve
  // callbacks (an upload batch that started before a conversation switch must
  // detect the switch at resolve time — see sendMessage's A2 guard).
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const [conversations, setConversations] = useState<GritClawConversation[]>(
    []
  );
  const [conversationRevision, setConversationRevision] = useState(0);
  const conversationRevisionRef = useRef(conversationRevision);
  const conversationsEpochRef = useRef(authEpoch);
  const [conversationListLoading, setConversationListLoading] = useState(false);
  const [conversationListError, setConversationListError] = useState(false);
  const [deletingConversationIds, setDeletingConversationIds] = useState<
    string[]
  >([]);
  const [clearingConversationHistory, setClearingConversationHistory] =
    useState(false);
  const conversationMutationUiEpochRef = useRef(authEpoch);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadError, setHistoryLoadError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const activeRunRef = useRef<string | null>(null);
  const conversationLifecycleRef = useRef(0);
  const conversationLifecycleFingerprintsRef = useRef<Map<string, string>>(
    new Map()
  );
  const missingConversationLifecycleIdsRef = useRef<Set<string>>(new Set());
  const activeConversationAuthEpochRef = useRef(0);
  const conversationListSeqRef = useRef(0);
  const lastHistoryFetchKeyRef = useRef('');
  const historyLoadSeqRef = useRef(0);
  const conversationsRef = useRef(conversations);
  const conversationsForCurrentEpoch =
    conversationsEpochRef.current === authEpoch ? conversations : [];
  conversationsRef.current = conversationsForCurrentEpoch;
  const conversationListStatusRef = useRef<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const selectedListedConversationRef = useRef<string | null>(null);
  const pendingUnavailableConversationRef = useRef<string | null>(null);
  const conversationDeletePromisesRef = useRef<Map<string, Promise<boolean>>>(
    new Map()
  );
  const conversationClearPromisesRef = useRef<Map<number, Promise<boolean>>>(
    new Map()
  );
  const deletingConversationIdsForCurrentEpoch =
    conversationMutationUiEpochRef.current === authEpoch
      ? deletingConversationIds
      : [];
  const clearingConversationHistoryForCurrentEpoch =
    conversationMutationUiEpochRef.current === authEpoch &&
    clearingConversationHistory;
  const conversationMutationPending =
    clearingConversationHistoryForCurrentEpoch ||
    deletingConversationIdsForCurrentEpoch.length > 0;
  const conversationMutationPendingRef = useRef(conversationMutationPending);
  conversationMutationPendingRef.current = conversationMutationPending;
  const runtimeUiEpochRef = useRef(authEpoch);
  const runtimeStateCurrent = runtimeUiEpochRef.current === authEpoch;
  const messagesForCurrentEpoch = runtimeStateCurrent ? messages : [];
  const isStreamingForCurrentEpoch = runtimeStateCurrent && isStreaming;
  const attachedContextForCurrentEpoch = runtimeStateCurrent
    ? attachedContext
    : [];
  const attachedFilesForCurrentEpoch = runtimeStateCurrent ? attachedFiles : [];
  const uploadingAttachmentsForCurrentEpoch =
    runtimeStateCurrent && uploadingAttachments;
  const overrideForCurrentEpoch = runtimeStateCurrent ? override : null;
  const screenContextSuppressedForCurrentEpoch =
    runtimeStateCurrent && screenContextSuppressed;
  const pendingItemsForCurrentEpoch = runtimeStateCurrent ? pendingItems : [];
  const inboxCountForCurrentEpoch = runtimeStateCurrent ? inboxCount : 0;
  const dismissedPinsForCurrentEpoch = runtimeStateCurrent
    ? dismissedPins
    : new Set<string>();
  const diagnosticsEnabledForCurrentEpoch =
    runtimeStateCurrent && diagnosticsEnabled;
  const diagnosticsRawEnabledForCurrentEpoch =
    runtimeStateCurrent && diagnosticsRawEnabled;
  messagesRef.current = messagesForCurrentEpoch;
  isStreamingRef.current = isStreamingForCurrentEpoch;
  uploadingAttachmentsRef.current = uploadingAttachmentsForCurrentEpoch;

  const advanceConversationLifecycle = useCallback(() => {
    conversationLifecycleRef.current += 1;
    return conversationLifecycleRef.current;
  }, []);

  // An owner change is a hard runtime boundary. The render above masks every
  // owner-scoped value immediately; this layout effect then detaches in-flight
  // work and clears backing state before the browser can paint the new owner.
  useLayoutEffect(() => {
    if (runtimeUiEpochRef.current === authEpoch) return;

    const oldController = abortRef.current;
    runtimeUiEpochRef.current = authEpoch;
    activeRunRef.current = null;
    abortRef.current = null;
    isStreamingRef.current = false;
    activeUploadRef.current = null;
    uploadBatchSeqRef.current += 1;
    uploadingAttachmentsRef.current = false;
    advanceConversationLifecycle();
    conversationLifecycleFingerprintsRef.current.clear();
    missingConversationLifecycleIdsRef.current.clear();
    wokeForThisOpenRef.current = false;
    selectedListedConversationRef.current = null;
    pendingUnavailableConversationRef.current = null;
    historyLoadSeqRef.current += 1;
    lastHistoryFetchKeyRef.current = '';

    const nextConversationId = getStoredGritClawConversationId();
    activeConversationIdRef.current = nextConversationId;
    activeConversationAuthEpochRef.current = authScopeKey ? authEpoch : 0;

    oldController?.abort();
    messagesRef.current = [];
    setMessages([]);
    setIsStreaming(false);
    setUploadingAttachments(false);
    setAttachedContext([]);
    setAttachedFiles([]);
    setOverride(null);
    setScreenContextSuppressed(false);
    setDismissedPins(new Set());
    setPendingItems([]);
    setInboxCount(0);
    setDiagnosticsEnabled(false);
    setDiagnosticsRawEnabled(false);
    setHistoryLoading(false);
    setHistoryLoadError(false);
    setActiveConversationIdState(nextConversationId);
  }, [advanceConversationLifecycle, authEpoch, authScopeKey]);

  // Reopen must re-arm wake: clear the per-open guard whenever the panel closes.
  // Done imperatively in the setter so a close re-arms even when React bails out
  // of a same-value re-render (the effect below is a safety net for open->close).
  const setPanelOpen = useCallback((open: boolean) => {
    if (!open) wokeForThisOpenRef.current = false;
    setPanelOpenState(open);
  }, []);

  useEffect(() => {
    if (!panelOpen) wokeForThisOpenRef.current = false;
  }, [panelOpen]);

  // Publish the docked-panel inset as a CSS custom property on :root. The app's
  // main content reserves this on its right margin and the command bar offsets
  // its centre by half of it, so both slide beside the panel in lockstep with
  // the drawer — driven by one variable, with no React re-render of the layout.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      GRITCLAW_INSET_VAR,
      panelOpen ? `${GRITCLAW_PANEL_WIDTH}px` : '0px'
    );
    return () => root.style.setProperty(GRITCLAW_INSET_VAR, '0px');
  }, [panelOpen]);

  const setActiveConversationId = useCallback((conversationId: string) => {
    const normalized = setStoredGritClawConversationId(conversationId);
    activeConversationIdRef.current = normalized;
    setActiveConversationIdState(normalized);
    return normalized;
  }, []);

  const advanceConversationRevision = useCallback(() => {
    const next = conversationRevisionRef.current + 1;
    conversationRevisionRef.current = next;
    setConversationRevision(next);
    return next;
  }, []);

  const resetConversation = useCallback(() => {
    // Starting a fresh conversation is not an explicit Stop: detach this
    // client, but let the server persist any in-flight answer.
    const oldController = abortRef.current;
    advanceConversationLifecycle();
    activeRunRef.current = null;
    abortRef.current = null;
    oldController?.abort();
    historyLoadSeqRef.current += 1;
    lastHistoryFetchKeyRef.current = '';
    selectedListedConversationRef.current = null;
    resetGritClawThreadId();
    setActiveConversationId(createGritClawConversationId());
    messagesRef.current = [];
    setMessages([]);
    isStreamingRef.current = false;
    setIsStreaming(false);
    setHistoryLoadError(false);
    setDismissedPins(new Set());
    setAttachedContext([]);
    setAttachedFiles([]);
  }, [advanceConversationLifecycle, setActiveConversationId]);

  const clearConversation = useCallback(() => {
    if (conversationMutationPendingRef.current) return;
    resetConversation();
  }, [resetConversation]);

  const pruneUnavailableConversation = useCallback(
    (conversationId: string) => {
      const id = normalizeGritClawConversationId(conversationId);
      const epoch = authEpochRef.current;
      if (pendingUnavailableConversationRef.current === id)
        pendingUnavailableConversationRef.current = null;
      // Invalidate any list read that began before this authoritative 404/410.
      // A later explicit refresh is allowed to show a legitimately recreated id.
      conversationListSeqRef.current += 1;
      setConversationListLoading(false);
      const next = conversationsRef.current.filter(
        (conversation) => conversation.conversation_id !== id
      );
      conversationsEpochRef.current = epoch;
      conversationsRef.current = next;
      selectedListedConversationRef.current = null;
      advanceConversationRevision();
      setConversations(next);
      setHistoryLoadError(false);
      showInfoRef.current(
        translateRef.current(
          'That conversation is no longer available, so it was removed from chat history.'
        )
      );
      if (activeConversationIdRef.current === id) resetConversation();
    },
    [advanceConversationRevision, resetConversation]
  );

  const handleHistoryLoadFailure = useCallback(
    (error: unknown, conversationId: string) => {
      const id = normalizeGritClawConversationId(conversationId);
      const status = requestStatus(error);
      const unavailable = status === 404 || status === 410;
      const wasListed =
        selectedListedConversationRef.current === id ||
        conversationsRef.current.some(
          (conversation) => conversation.conversation_id === id
        );

      if (unavailable && wasListed) {
        pruneUnavailableConversation(id);
        return;
      }
      // A new local draft has no persisted history yet and may legitimately
      // resolve 404. Defer the decision until an in-flight list request tells
      // us whether the server advertised this id as a real conversation.
      if (unavailable) {
        pendingUnavailableConversationRef.current =
          conversationListStatusRef.current === 'success' ? null : id;
        setHistoryLoadError(false);
        return;
      }
      setHistoryLoadError(true);
    },
    [pruneUnavailableConversation]
  );

  const refreshConversations = useCallback(() => {
    const epoch = authEpoch;
    // A retained callback from an older owner must be a complete no-op. In
    // particular, it must not advance the shared sequence or flip B's loading
    // state before its own request completes.
    if (authEpochRef.current !== epoch) return;
    const seq = conversationListSeqRef.current + 1;
    conversationListSeqRef.current = seq;
    if (!authScopeKey) {
      conversationListStatusRef.current = 'idle';
      conversationsEpochRef.current = epoch;
      conversationsRef.current = [];
      setConversations([]);
      setConversationListLoading(false);
      setConversationListError(false);
      return;
    }
    conversationListStatusRef.current = 'loading';
    setConversationListLoading(true);
    setConversationListError(false);
    void fetchConversations()
      .then((rows) => {
        if (
          conversationListSeqRef.current !== seq ||
          authEpochRef.current !== epoch
        )
          return;
        const activeId = activeConversationIdRef.current;
        const incomingIds = new Set(
          rows.map((conversation) => conversation.conversation_id)
        );
        const incomingActive = rows.find(
          (conversation) => conversation.conversation_id === activeId
        );
        const previousActiveFingerprint =
          conversationLifecycleFingerprintsRef.current.get(activeId);
        const incomingActiveFingerprint = incomingActive
          ? conversationLifecycleFingerprint(incomingActive)
          : undefined;
        const activeLifecycleChanged =
          (previousActiveFingerprint !== undefined &&
            (incomingActiveFingerprint === undefined ||
              previousActiveFingerprint !== incomingActiveFingerprint ||
              // Without an immutable creation timestamp equality cannot prove
              // continuity, so an authoritative refresh fails closed.
              previousActiveFingerprint ===
                UNKNOWN_CONVERSATION_LIFECYCLE_FINGERPRINT)) ||
          (previousActiveFingerprint === undefined &&
            incomingActiveFingerprint !== undefined &&
            missingConversationLifecycleIdsRef.current.has(activeId));
        if (activeLifecycleChanged) advanceConversationLifecycle();

        for (const knownId of conversationLifecycleFingerprintsRef.current.keys()) {
          if (!incomingIds.has(knownId)) {
            conversationLifecycleFingerprintsRef.current.delete(knownId);
            missingConversationLifecycleIdsRef.current.add(knownId);
          }
        }
        for (const conversation of rows) {
          conversationLifecycleFingerprintsRef.current.set(
            conversation.conversation_id,
            conversationLifecycleFingerprint(conversation)
          );
          missingConversationLifecycleIdsRef.current.delete(
            conversation.conversation_id
          );
        }

        conversationListStatusRef.current = 'success';
        conversationsEpochRef.current = epoch;
        conversationsRef.current = rows;
        advanceConversationRevision();
        setConversations(rows);

        const pendingUnavailableId = pendingUnavailableConversationRef.current;
        pendingUnavailableConversationRef.current = null;
        if (
          pendingUnavailableId &&
          rows.some(
            (conversation) =>
              conversation.conversation_id === pendingUnavailableId
          )
        ) {
          pruneUnavailableConversation(pendingUnavailableId);
        }
      })
      .catch(() => {
        if (
          conversationListSeqRef.current === seq &&
          authEpochRef.current === epoch
        ) {
          conversationListStatusRef.current = 'error';
          setConversationListError(true);
        }
      })
      .finally(() => {
        if (
          conversationListSeqRef.current === seq &&
          authEpochRef.current === epoch
        )
          setConversationListLoading(false);
      });
  }, [
    advanceConversationRevision,
    advanceConversationLifecycle,
    authEpoch,
    authScopeKey,
    pruneUnavailableConversation,
  ]);

  useEffect(() => {
    conversationListSeqRef.current += 1;
    pendingUnavailableConversationRef.current = null;
    selectedListedConversationRef.current = null;
    conversationLifecycleFingerprintsRef.current.clear();
    missingConversationLifecycleIdsRef.current.clear();
    conversationListStatusRef.current = 'idle';
    conversationsEpochRef.current = authEpoch;
    conversationsRef.current = [];
    advanceConversationRevision();
    setConversations([]);
    conversationMutationUiEpochRef.current = authEpoch;
    conversationMutationPendingRef.current = false;
    setDeletingConversationIds([]);
    setClearingConversationHistory(false);
    if (!authScopeKey) {
      activeConversationAuthEpochRef.current = 0;
      lastHistoryFetchKeyRef.current = '';
      setConversationListLoading(false);
      setConversationListError(false);
      return;
    }
    refreshConversations();
  }, [
    advanceConversationRevision,
    authEpoch,
    authScopeKey,
    refreshConversations,
  ]);

  useEffect(() => {
    const epoch = authEpoch;
    const seq = historyLoadSeqRef.current + 1;
    historyLoadSeqRef.current = seq;
    if (!authScopeKey) {
      activeConversationAuthEpochRef.current = 0;
      lastHistoryFetchKeyRef.current = '';
      setMessages([]);
      setHistoryLoading(false);
      setHistoryLoadError(false);
      return;
    }
    // Layout auth reset may have synchronously moved the ref before this
    // passive effect from the pre-reset render runs. Always trust the ref so
    // an anonymous/A id is never fetched under the new owner's credentials.
    let conversationId = activeConversationIdRef.current;
    if (activeConversationAuthEpochRef.current !== epoch) {
      conversationId = getStoredGritClawConversationId();
      activeConversationIdRef.current = conversationId;
      activeConversationAuthEpochRef.current = epoch;
      setMessages([]);
      if (conversationId !== activeConversationId)
        setActiveConversationIdState(conversationId);
    }
    const historyFetchKey = `${epoch}:${conversationId}`;
    if (lastHistoryFetchKeyRef.current === historyFetchKey) {
      setHistoryLoading(false);
      return;
    }
    lastHistoryFetchKeyRef.current = historyFetchKey;
    setHistoryLoading(true);
    setHistoryLoadError(false);
    void fetchHistory(40, conversationId)
      .then((history) => {
        if (historyLoadSeqRef.current !== seq || authEpochRef.current !== epoch)
          return;
        setHistoryLoadError(false);
        setMessages((prev) => {
          if (!history.length) {
            const liveTurnInProgress = prev.some(
              (m) => m.role === 'user' || m.streaming
            );
            const wakeMessagesVisible = prev.some(
              (m) => m.role === 'assistant'
            );
            return liveTurnInProgress || wakeMessagesVisible ? prev : [];
          }
          const hydrated = historyToMessages(history);
          if (!prev.length) return hydrated;
          // If wake resolved first, prev contains only assistant greetings (no
          // user messages). Insert the history BELOW the greetings so the user's
          // prior conversation is still visible; greetings remain on top.
          const onlyWakeGreetings = prev.every(
            (m) => m.role === 'assistant' && !m.streaming
          );
          if (onlyWakeGreetings) return [...prev, ...hydrated];
          // History (or a live turn) already present — don't overwrite.
          return prev;
        });
      })
      .catch((error) => {
        if (historyLoadSeqRef.current === seq && authEpochRef.current === epoch)
          handleHistoryLoadFailure(error, conversationId);
      })
      .finally(() => {
        if (historyLoadSeqRef.current === seq && authEpochRef.current === epoch)
          setHistoryLoading(false);
      });
  }, [activeConversationId, authEpoch, authScopeKey, handleHistoryLoadFailure]);

  const withPinnedContext = useCallback(
    (context: ScreenContext): ScreenContext => {
      if (!attachedContextForCurrentEpoch.length) return context;
      return {
        ...context,
        selection: {
          ...(context.selection ?? {}),
          attached_context: attachedContextForCurrentEpoch.map((ref) => ({
            kind: ref.kind,
            id: ref.id,
          })),
        },
      };
    },
    [attachedContextForCurrentEpoch]
  );

  const currentScreenContext = useCallback((): ScreenContext => {
    const derived = deriveScreenContext(
      location.pathname,
      overrideForCurrentEpoch ?? undefined
    );
    return withPinnedContext(
      screenContextSuppressedForCurrentEpoch
        ? {
            ...derived,
            selection: undefined,
            filters: undefined,
            visible_summary: undefined,
          }
        : derived
    );
  }, [
    location.pathname,
    overrideForCurrentEpoch,
    screenContextSuppressedForCurrentEpoch,
    withPinnedContext,
  ]);

  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      setOverride(null);
      setScreenContextSuppressed(false);
    }
  }, [location.pathname]);

  const setScreenOverride = useCallback((o: ScreenOverride | null) => {
    setOverride(o);
    setScreenContextSuppressed(false);
  }, []);

  const detachScreenContext = useCallback(() => {
    setOverride(null);
    setScreenContextSuppressed(true);
  }, []);

  const attachContext = useCallback((refs: ContextRef[]) => {
    setAttachedContext((prev) => {
      const byId = new Map(prev.map((ref) => [`${ref.kind}:${ref.id}`, ref]));
      for (const ref of refs) byId.set(`${ref.kind}:${ref.id}`, ref);
      return Array.from(byId.values());
    });
  }, []);

  const detachContext = useCallback((id: string) => {
    setAttachedContext((prev) => prev.filter((ref) => ref.id !== id));
  }, []);

  const clearContext = useCallback(() => {
    setAttachedContext([]);
  }, []);

  const attachFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      // Fail fast on the obviously-wrong (over 25 MB / unsupported type) before
      // staging or uploading; the gateway remains the authority on the rest.
      const accepted: File[] = [];
      const rejected: { file: File; reason: AttachmentRejectReason }[] = [];
      for (const file of files) {
        const v = validateAttachmentFile(file);
        if (v.ok) accepted.push(file);
        else rejected.push({ file, reason: v.reason });
      }
      if (rejected.length === 1) {
        const { file, reason } = rejected[0];
        snackbar.showWarning(
          reason === 'too_large'
            ? t('{{name}} is larger than 25 MB and was not attached.', {
                name: file.name,
              })
            : t('{{name}} is not a supported file type and was not attached.', {
                name: file.name,
              })
        );
      } else if (rejected.length > 1) {
        snackbar.showWarning(
          t(
            '{{count}} files were not attached (unsupported type or over 25 MB).',
            { count: rejected.length }
          )
        );
      }
      if (!accepted.length) return;
      setAttachedFiles((prev) => {
        const seen = new Set(
          prev.map((f) => `${f.name}:${f.size}:${f.file.lastModified}`)
        );
        const next = [...prev];
        for (const file of accepted) {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          if (seen.has(key)) continue;
          seen.add(key);
          next.push({
            id: newId(),
            name: file.name,
            size: file.size,
            type: file.type,
            file,
          });
        }
        return next;
      });
    },
    [snackbar, t]
  );

  const detachFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setAttachedFiles([]);
  }, []);

  const dismissPin = useCallback((id: string) => {
    setDismissedPins((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const focusBar = useCallback(() => setFocusBarNonce((n) => n + 1), []);

  // Full-page handoff: collapse the bar, then after the medium transition window
  // navigate to the dedicated GritClaw page and clear the transitioning flag.
  const requestFull = useCallback(() => {
    setFullTransitioning(true);
    setBarExpanded(false);
    window.setTimeout(() => {
      navigate('/gritclaw');
      setFullTransitioning(false);
    }, MOTION.durationMedium);
  }, [navigate]);

  const patchAssistant = useCallback(
    (id: string, patch: (m: ChatMessage) => ChatMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
    },
    []
  );

  const selectConversation = useCallback(
    (conversationId: string) => {
      if (conversationMutationPendingRef.current) return;
      const rawId = String(conversationId ?? '').trim();
      if (!rawId) return;
      const epoch = authEpochRef.current;
      const normalized = normalizeGritClawConversationId(rawId);
      const currentRows =
        conversationsEpochRef.current === epoch ? conversationsRef.current : [];
      const listed = currentRows.some(
        (conversation) => conversation.conversation_id === normalized
      );
      // Navigation targets come from the current epoch's authoritative list.
      // The current local draft remains retryable even before it is persisted.
      if (!listed && normalized !== activeConversationIdRef.current) return;
      // This is a navigation/lifecycle boundary even when the logical id is
      // reused (main -> other -> main, or re-selecting a recreated main).
      advanceConversationLifecycle();
      selectedListedConversationRef.current = listed ? normalized : null;
      if (normalized === activeConversationIdRef.current) {
        if (activeRunRef.current) return;
        const seq = historyLoadSeqRef.current + 1;
        historyLoadSeqRef.current = seq;
        setHistoryLoading(true);
        setHistoryLoadError(false);
        void fetchHistory(100, normalized)
          .then((history) => {
            if (
              historyLoadSeqRef.current !== seq ||
              authEpochRef.current !== epoch
            )
              return;
            setHistoryLoadError(false);
            if (history.length) setMessages(historyToMessages(history));
          })
          .catch((error) => {
            if (
              historyLoadSeqRef.current === seq &&
              authEpochRef.current === epoch
            )
              handleHistoryLoadFailure(error, normalized);
          })
          .finally(() => {
            if (
              historyLoadSeqRef.current === seq &&
              authEpochRef.current === epoch
            )
              setHistoryLoading(false);
          });
        return;
      }
      // Conversation navigation only detaches this client stream. The server
      // keeps working and persists the answer; only explicit Stop cancels it.
      const oldController = abortRef.current;
      activeRunRef.current = null;
      abortRef.current = null;
      oldController?.abort();
      resetGritClawThreadId();
      setActiveConversationId(normalized);
      messagesRef.current = [];
      setMessages([]);
      isStreamingRef.current = false;
      setIsStreaming(false);
      setHistoryLoadError(false);
      setDismissedPins(new Set());
      setAttachedContext([]);
      // A conversation switch aborts an in-flight attachment send. Keep those
      // staged files so the operator can resend them in the selected thread.
      if (!uploadingAttachmentsRef.current) setAttachedFiles([]);
    },
    [
      advanceConversationLifecycle,
      handleHistoryLoadFailure,
      setActiveConversationId,
    ]
  );

  const runTurn = useCallback(
    (
      sendText: string,
      displayText: string | undefined,
      userVisible: boolean,
      // Override to use for THIS turn's screen_context. Needed when the override
      // was set in the same React event as the turn: setScreenOverride only
      // schedules a state update, so currentScreenContext() would still read the
      // stale (pre-commit) override. Falls back to currentScreenContext().
      overrideForTurn?: ScreenOverride,
      // Uploaded attachment ids forwarded to the gateway for this turn, plus the
      // file metadata to show as chips under the user's sent bubble.
      attachmentIds: string[] = [],
      userAttachments?: UserAttachmentMeta[]
    ) => {
      const epoch = authEpoch;
      const conversationId = activeConversationId;
      if (
        conversationMutationPendingRef.current ||
        !authScopeKey ||
        authEpochRef.current !== epoch ||
        activeConversationIdRef.current !== conversationId ||
        activeRunRef.current !== null
      )
        return false;
      const assistantId = newId();
      const runId = createGritClawRunId();
      const expectedMatchingUserCount =
        messagesRef.current.filter(
          (message) => message.role === 'user' && message.text === sendText
        ).length + 1;
      setMessages((prev) => [
        ...prev,
        ...(userVisible
          ? [
              {
                id: newId(),
                role: 'user' as const,
                text: displayText ?? sendText,
                uiBlocks: [],
                activeTools: [],
                streaming: false,
                ...(userAttachments && userAttachments.length
                  ? { attachments: userAttachments }
                  : {}),
              },
            ]
          : []),
        {
          id: assistantId,
          role: 'assistant' as const,
          text: '',
          uiBlocks: [],
          activeTools: [],
          streaming: true,
          runId,
          progress: { label: t('Thinking'), phase: 'thinking' },
        },
      ]);
      isStreamingRef.current = true;
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      activeRunRef.current = runId;
      const ownsRun = () =>
        authEpochRef.current === epoch &&
        activeConversationIdRef.current === conversationId &&
        activeRunRef.current === runId &&
        abortRef.current === controller;

      const screenContext = overrideForTurn
        ? withPinnedContext(
            deriveScreenContext(location.pathname, overrideForTurn)
          )
        : currentScreenContext();

      const reconcileDisconnectedTurn = async (): Promise<void> => {
        if (!ownsRun()) return;
        patchAssistant(assistantId, (m) => ({
          ...m,
          progress: {
            label: t('Reconnecting to your response'),
            phase: 'reconnect',
          },
          error: undefined,
        }));
        const initialDelays = [1500, 2500, 4000, 6000, 8000];
        const deadline = Date.now() + 10 * 60 * 1000;
        let attempt = 0;
        while (Date.now() < deadline) {
          const delay = initialDelays[attempt] ?? 10000;
          attempt += 1;
          await new Promise<void>((resolve) =>
            window.setTimeout(resolve, delay)
          );
          if (!ownsRun()) return;
          let history: HistoryMessage[];
          try {
            history = await fetchHistory(100, conversationId);
          } catch {
            // Reconciliation is a bounded poll. A transient history read must
            // consume one attempt, not abort the whole recovery loop.
            continue;
          }
          if (!ownsRun()) return;
          let lastUser = -1;
          let matchingUserCount = 0;
          history.forEach((entry, index) => {
            if (entry.role === 'user' && entry.text === sendText) {
              matchingUserCount += 1;
              lastUser = index;
            }
          });
          const completed =
            lastUser >= 0 &&
            matchingUserCount >= expectedMatchingUserCount &&
            history
              .slice(lastUser + 1)
              .some(
                (entry) =>
                  entry.role === 'assistant' &&
                  (Boolean(entry.text) || Boolean(entry.ui_blocks?.length))
              );
          if (completed) {
            if (!ownsRun()) return;
            const hydrated = historyToMessages(history);
            messagesRef.current = hydrated;
            setMessages(hydrated);
            return;
          }
        }
        if (!ownsRun()) return;
        patchAssistant(assistantId, (m) => ({
          ...m,
          streaming: false,
          progress: undefined,
          error: t(
            'The connection was interrupted. Refresh this conversation to check for the completed response.'
          ),
        }));
      };

      const handlers: AgUiHandlers = {
        onTextDelta: (delta) => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({
            ...m,
            text: m.text + delta,
            progress: undefined,
          }));
        },
        onUiBlock: (payload) => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({
            ...m,
            uiBlocks: [...m.uiBlocks, payload],
            progress: undefined,
          }));
        },
        onProgress: (label, phase) => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({
            ...m,
            progress: { label, phase },
          }));
        },
        onCorrelationId: (corrId) => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({ ...m, corrId }));
        },
        onDisconnected: async () => {
          if (!ownsRun()) return;
          await reconcileDisconnectedTurn();
        },
        onError: (message) => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({
            ...m,
            streaming: false,
            error: message,
            text: m.text,
            progress: undefined,
          }));
        },
        onFinished: () => {
          if (!ownsRun()) return;
          patchAssistant(assistantId, (m) => ({
            ...m,
            streaming: false,
            activeTools: [],
            progress: undefined,
          }));
        },
      };

      // Keep the no-attachment turn at its original 6-arg call shape; only widen
      // the signature to pass attachment ids when this turn actually carries them.
      const run =
        attachmentIds.length > 0
          ? streamRun(
              sendText,
              screenContext,
              handlers,
              controller.signal,
              undefined,
              conversationId,
              attachmentIds,
              runId
            )
          : streamRun(
              sendText,
              screenContext,
              handlers,
              controller.signal,
              undefined,
              conversationId,
              [],
              runId
            );

      void run.finally(() => {
        if (!ownsRun()) return;
        patchAssistant(assistantId, (m) => ({
          ...m,
          streaming: false,
          activeTools: [],
          progress: undefined,
        }));
        isStreamingRef.current = false;
        setIsStreaming(false);
        abortRef.current = null;
        activeRunRef.current = null;
        refreshConversations();
      });
      return true;
    },
    [
      activeConversationId,
      authEpoch,
      authScopeKey,
      currentScreenContext,
      location.pathname,
      patchAssistant,
      refreshConversations,
      t,
      withPinnedContext,
    ]
  );

  const stopTurn = useCallback(() => {
    const runId = activeRunRef.current;
    const controller = abortRef.current;
    const epoch = authEpochRef.current;
    if (!runId || !controller) return;
    void cancelGritClawRun(runId)
      .then(() => {
        if (
          authEpochRef.current !== epoch ||
          activeRunRef.current !== runId ||
          abortRef.current !== controller
        )
          return;
        controller.abort();
        abortRef.current = null;
        activeRunRef.current = null;
        isStreamingRef.current = false;
        setIsStreaming(false);
        setMessages((prev) =>
          prev.map((message) =>
            message.runId === runId
              ? {
                  ...message,
                  streaming: false,
                  activeTools: [],
                  progress: undefined,
                }
              : message
          )
        );
      })
      .catch(() => undefined);
  }, []);

  const retryMessage = useCallback(
    (messageId: string) => {
      if (isStreamingRef.current || conversationMutationPendingRef.current)
        return;
      const failedIndex = messages.findIndex(
        (message) =>
          message.id === messageId &&
          message.role === 'assistant' &&
          message.error
      );
      if (failedIndex < 0) return;
      const userMessage = [...messages.slice(0, failedIndex)]
        .reverse()
        .find((message) => message.role === 'user');
      if (!userMessage?.text.trim()) return;
      setMessages((prev) => prev.filter((message) => message.id !== messageId));
      runTurn(userMessage.text, undefined, false);
    },
    [isStreaming, messages, runTurn]
  );

  // Turn an upload failure (typed AttachmentUploadError, duck-typed here so a
  // mocked client module can't break the check) into a filename-aware toast.
  const uploadErrorMessage = useCallback(
    (err: unknown): string => {
      const detail =
        err && typeof err === 'object'
          ? (err as { code?: unknown; filename?: unknown })
          : {};
      const code = typeof detail.code === 'string' ? detail.code : undefined;
      const name = typeof detail.filename === 'string' ? detail.filename : '';
      switch (code) {
        case 'too_large':
          return t("Couldn't upload {{name}}. It is larger than 25 MB.", {
            name,
          });
        case 'unsupported_type':
          return t(
            "Couldn't upload {{name}}. That file type is not supported.",
            {
              name,
            }
          );
        case 'unauthorized':
          return t('Your session has expired. Please sign in again.');
        default:
          return name
            ? t("Couldn't upload {{name}}. Please try again.", { name })
            : t("Couldn't upload your files. Please try again.");
      }
    },
    [t]
  );

  const sendMessage = useCallback(
    (text: string, displayText?: string): boolean => {
      const trimmed = text.trim();
      if (
        !trimmed ||
        !authScopeKey ||
        authEpochRef.current !== authEpoch ||
        activeConversationIdRef.current !== activeConversationId ||
        isStreamingRef.current ||
        uploadingAttachmentsRef.current ||
        conversationMutationPendingRef.current
      )
        return false;

      // No files: the fast synchronous path (unchanged).
      if (attachedFilesForCurrentEpoch.length === 0) {
        return runTurn(trimmed, displayText, true);
      }

      // Cap the per-message attachment count — block (don't silently drop) if over.
      if (attachedFilesForCurrentEpoch.length > MAX_ATTACHMENTS_PER_MESSAGE) {
        snackbar.showWarning(
          t('You can attach up to {{max}} files per message.', {
            max: MAX_ATTACHMENTS_PER_MESSAGE,
          })
        );
        return false;
      }

      // Snapshot everything this send owns: the exact staged files, their ids,
      // the metadata for the sent-bubble chips, and the conversation it targets.
      const staged = attachedFilesForCurrentEpoch;
      const stagedIds = new Set(staged.map((f) => f.id));
      const userAttachments: UserAttachmentMeta[] = staged.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      }));
      const sendConversationId = activeConversationId;
      const sendConversationLifecycle = conversationLifecycleRef.current;
      const uploadEpoch = authEpoch;
      const uploadSeq = uploadBatchSeqRef.current + 1;
      uploadBatchSeqRef.current = uploadSeq;
      activeUploadRef.current = { epoch: uploadEpoch, seq: uploadSeq };
      const ownsUpload = () =>
        authEpochRef.current === uploadEpoch &&
        activeUploadRef.current?.epoch === uploadEpoch &&
        activeUploadRef.current?.seq === uploadSeq;

      uploadingAttachmentsRef.current = true;
      setUploadingAttachments(true);
      // allSettled (not all): a partial failure must keep the already-succeeded
      // upload ids so a retry doesn't re-upload them. A file that already carries
      // an `uploadedId` (succeeded on a prior attempt this session) is reused
      // as-is. Result order matches `staged`.
      void Promise.allSettled(
        staged.map((f) =>
          f.uploadedId
            ? Promise.resolve(f.uploadedId)
            : uploadAttachment(f.file).then((u) => u.upload_id)
        )
      )
        .then((results) => {
          if (!ownsUpload()) return;
          const succeededById = new Map<string, string>();
          const failed: { file: FileRef; reason: unknown }[] = [];
          results.forEach((r, i) => {
            const f = staged[i];
            if (r.status === 'fulfilled') succeededById.set(f.id, r.value);
            else failed.push({ file: f, reason: r.reason });
          });

          // Cache successful ids on the still-staged FileRefs (id-keyed) so a
          // retry after fixing/removing the failed file reuses them.
          if (succeededById.size) {
            setAttachedFiles((prev) =>
              prev.map((f) =>
                succeededById.has(f.id)
                  ? { ...f, uploadedId: succeededById.get(f.id) }
                  : f
              )
            );
          }

          // A3: any failure blocks the send and keeps every chip. Name the
          // offending file(s); the cached successes above make the retry cheap.
          if (failed.length) {
            snackbar.showError(
              failed.length === 1
                ? uploadErrorMessage(failed[0].reason)
                : t("Couldn't upload {{names}}. Please try again.", {
                    names: failed.map((x) => x.file.name).join(', '),
                  })
            );
            return;
          }

          // A2: the conversation changed while the batch was in flight — do NOT
          // leak this turn into the newly-selected thread. Keep the (now-cached)
          // chips; the operator resends in whichever conversation they meant.
          if (
            activeConversationIdRef.current !== sendConversationId ||
            conversationLifecycleRef.current !== sendConversationLifecycle
          ) {
            snackbar.showInfo(
              t('Message not sent — the conversation changed.')
            );
            return;
          }

          // A destructive mutation may have started while uploads resolved.
          // Keep the cached chips and fail closed; runTurn repeats this guard.
          if (conversationMutationPendingRef.current) return;

          // All uploaded, same conversation — clear only THIS send's staged ids
          // (filter, don't clobber) so files attached mid-upload for the next
          // message survive, then run the turn.
          const attachmentIds = staged.map(
            (f) => succeededById.get(f.id) as string
          );
          const accepted = runTurn(
            trimmed,
            displayText,
            true,
            undefined,
            attachmentIds,
            userAttachments
          );
          if (!accepted) return;
          setAttachedFiles((prev) => prev.filter((f) => !stagedIds.has(f.id)));
        })
        .finally(() => {
          if (!ownsUpload()) return;
          activeUploadRef.current = null;
          uploadingAttachmentsRef.current = false;
          setUploadingAttachments(false);
        });
      return true;
    },
    [
      activeConversationId,
      attachedFilesForCurrentEpoch,
      authEpoch,
      authScopeKey,
      runTurn,
      snackbar,
      t,
      uploadErrorMessage,
    ]
  );

  const askGritClawAbout = useCallback(
    (selection: Record<string, unknown>, promptId?: AskPromptId) => {
      if (conversationMutationPendingRef.current) return;
      const seed = buildAskGritClawSeed(selection, promptId);
      setScreenOverride(seed.override);
      setPanelOpen(true);
      // Pass the override into this turn explicitly: setScreenOverride only
      // schedules a re-render, so the turn would otherwise send the stale
      // (selection-less) screen_context for the route.
      if (seed.sendText)
        runTurn(seed.sendText, seed.displayText, true, seed.override);
    },
    [setScreenOverride, setPanelOpen, runTurn]
  );

  const confirmAction = useCallback(
    (d: ConfirmDecision) => {
      // The decision rides back as the next user message (contract §confirm_action),
      // shown as a friendly bubble rather than raw JSON. Plain text, no emoji.
      runTurn(
        JSON.stringify(d),
        d.decision === 'approve' ? 'Approved' : 'Declined',
        true
      );
    },
    [runTurn]
  );

  // Rename/pin update optimistically, then re-sync so server ordering is
  // authoritative. Deletes deliberately keep local state until the server has
  // confirmed the destructive operation.
  const renameConversation = useCallback(
    (conversationId: string, title: string) => {
      const epoch = authEpochRef.current;
      const id = normalizeGritClawConversationId(conversationId);
      const trimmed = title.trim();
      if (
        !trimmed ||
        conversationsEpochRef.current !== epoch ||
        !conversationsRef.current.some((c) => c.conversation_id === id)
      )
        return;
      const next = conversationsRef.current.map((c) =>
        c.conversation_id === id ? { ...c, title: trimmed } : c
      );
      conversationsRef.current = next;
      advanceConversationRevision();
      setConversations(next);
      void updateConversation(id, { title: trimmed }).then(() =>
        refreshConversations()
      );
    },
    [advanceConversationRevision, refreshConversations]
  );

  const setConversationPinned = useCallback(
    (conversationId: string, pinned: boolean) => {
      const epoch = authEpochRef.current;
      const id = normalizeGritClawConversationId(conversationId);
      if (
        conversationsEpochRef.current !== epoch ||
        !conversationsRef.current.some((c) => c.conversation_id === id)
      )
        return;
      const next = conversationsRef.current.map((c) =>
        c.conversation_id === id ? { ...c, pinned } : c
      );
      conversationsRef.current = next;
      advanceConversationRevision();
      setConversations(next);
      void updateConversation(id, { pinned }).then(() =>
        refreshConversations()
      );
    },
    [advanceConversationRevision, refreshConversations]
  );

  const removeConversation = useCallback(
    (
      conversationId: string,
      expectedEpoch: number,
      expectedRevision: number
    ): Promise<boolean> => {
      const rawId = String(conversationId ?? '').trim();
      if (!rawId) return Promise.resolve(false);
      const epoch = authEpochRef.current;
      const id = normalizeGritClawConversationId(rawId);
      if (
        !authScopeKey ||
        expectedEpoch !== epoch ||
        expectedRevision !== conversationRevisionRef.current ||
        conversationsEpochRef.current !== epoch ||
        !conversationsRef.current.some(
          (conversation) => conversation.conversation_id === id
        )
      )
        return Promise.resolve(false);

      const key = `${epoch}\u0000${id}`;
      const existing = conversationDeletePromisesRef.current.get(key);
      if (existing) return existing;
      const anotherDeletePending = Array.from(
        conversationDeletePromisesRef.current.keys()
      ).some((pendingKey) => pendingKey.startsWith(`${epoch}\u0000`));
      if (
        conversationClearPromisesRef.current.has(epoch) ||
        anotherDeletePending ||
        isStreamingRef.current ||
        uploadingAttachmentsRef.current
      )
        return Promise.resolve(false);

      conversationMutationUiEpochRef.current = epoch;
      conversationMutationPendingRef.current = true;
      setDeletingConversationIds((prev) =>
        prev.includes(id) ? prev : [...prev, id]
      );
      const request = deleteConversation(id)
        .then(() => {
          if (authEpochRef.current !== epoch) return true;
          // A newer authoritative list/revision owns the UI now. Never apply
          // this older confirmation to its rows or active draft; reconcile the
          // server result with a fresh read instead.
          if (conversationRevisionRef.current !== expectedRevision) {
            refreshConversations();
            return true;
          }
          // Ignore every list result that began before the DELETE committed.
          // A later refresh gets a new sequence and may show a recreated id.
          conversationListSeqRef.current += 1;
          conversationListStatusRef.current = 'success';
          setConversationListLoading(false);
          if (pendingUnavailableConversationRef.current === id)
            pendingUnavailableConversationRef.current = null;
          const next = conversationsRef.current.filter(
            (conversation) => conversation.conversation_id !== id
          );
          conversationsEpochRef.current = epoch;
          conversationsRef.current = next;
          advanceConversationRevision();
          setConversations(next);
          if (activeConversationIdRef.current === id) resetConversation();
          return true;
        })
        .catch(() => {
          if (authEpochRef.current === epoch) {
            showErrorRef.current(
              translateRef.current(
                "We couldn't confirm that conversation was deleted. Refresh chat history before trying again."
              )
            );
          }
          return false;
        })
        .finally(() => {
          conversationDeletePromisesRef.current.delete(key);
          if (authEpochRef.current === epoch) {
            setDeletingConversationIds((prev) =>
              prev.filter((conversationId) => conversationId !== id)
            );
            conversationMutationPendingRef.current =
              conversationClearPromisesRef.current.has(epoch) ||
              Array.from(conversationDeletePromisesRef.current.keys()).some(
                (pendingKey) => pendingKey.startsWith(`${epoch}\u0000`)
              );
          }
        });
      conversationDeletePromisesRef.current.set(key, request);
      return request;
    },
    [
      advanceConversationRevision,
      authScopeKey,
      refreshConversations,
      resetConversation,
    ]
  );

  const clearConversationHistory = useCallback(
    (expectedEpoch: number, expectedRevision: number): Promise<boolean> => {
      const epoch = authEpochRef.current;
      if (
        !authScopeKey ||
        expectedEpoch !== epoch ||
        expectedRevision !== conversationRevisionRef.current ||
        conversationsEpochRef.current !== epoch ||
        conversationsRef.current.length === 0
      )
        return Promise.resolve(false);
      const existing = conversationClearPromisesRef.current.get(epoch);
      if (existing) return existing;
      const deletePending = Array.from(
        conversationDeletePromisesRef.current.keys()
      ).some((key) => key.startsWith(`${epoch}\u0000`));
      if (
        deletePending ||
        isStreamingRef.current ||
        uploadingAttachmentsRef.current
      )
        return Promise.resolve(false);

      conversationMutationUiEpochRef.current = epoch;
      conversationMutationPendingRef.current = true;
      setClearingConversationHistory(true);

      const request = deleteAllConversations()
        .then(() => {
          if (authEpochRef.current !== epoch) return true;
          // The clear confirmation belongs to the revision captured by its
          // dialog. Preserve any newer list/draft and reconcile from the API.
          if (conversationRevisionRef.current !== expectedRevision) {
            refreshConversations();
            return true;
          }
          conversationListSeqRef.current += 1;
          conversationListStatusRef.current = 'success';
          historyLoadSeqRef.current += 1;
          lastHistoryFetchKeyRef.current = '';
          pendingUnavailableConversationRef.current = null;
          conversationsEpochRef.current = epoch;
          conversationsRef.current = [];
          advanceConversationRevision();
          setConversations([]);
          setConversationListLoading(false);
          setConversationListError(false);
          resetConversation();
          return true;
        })
        .catch(() => {
          if (authEpochRef.current === epoch) {
            showErrorRef.current(
              translateRef.current(
                "We couldn't confirm chat history was cleared. Refresh chat history before trying again."
              )
            );
          }
          return false;
        })
        .finally(() => {
          if (conversationClearPromisesRef.current.get(epoch) === request)
            conversationClearPromisesRef.current.delete(epoch);
          if (authEpochRef.current === epoch) {
            setClearingConversationHistory(false);
            conversationMutationPendingRef.current = Array.from(
              conversationDeletePromisesRef.current.keys()
            ).some((key) => key.startsWith(`${epoch}\u0000`));
          }
        });
      conversationClearPromisesRef.current.set(epoch, request);
      return request;
    },
    [
      advanceConversationRevision,
      authScopeKey,
      refreshConversations,
      resetConversation,
    ]
  );

  const wakeIfNeeded = useCallback(() => {
    const epoch = authEpoch;
    if (
      !authScopeKey ||
      authEpochRef.current !== epoch ||
      wokeForThisOpenRef.current
    )
      return;
    wokeForThisOpenRef.current = true;
    void wakeOnOpen(currentScreenContext()).then((res) => {
      if (!res || authEpochRef.current !== epoch) return;
      setMessages((prev) => {
        const next = mergeWake(res, prev);
        messagesRef.current = next;
        return next;
      });
      // Opening the chat resolves the inbox: pending shown -> badge clears.
      setInboxCount(0);
    });
  }, [authEpoch, authScopeKey, currentScreenContext]);

  // Badge refresh: poll the non-consuming inbox so the launcher shows a count
  // even while the panel is closed (no turn spent, no rate-limit slot consumed).
  const refreshInbox = useCallback(() => {
    const epoch = authEpoch;
    if (authEpochRef.current !== epoch) return;
    if (!authScopeKey) {
      setPendingItems([]);
      setInboxCount(0);
      return;
    }
    void fetchInbox().then((pending) => {
      if (authEpochRef.current !== epoch) return;
      setPendingItems(pending);
      if (!panelOpen) setInboxCount(pending.length);
    });
  }, [authEpoch, authScopeKey, panelOpen]);

  useEffect(() => {
    const epoch = authEpoch;
    let active = true;
    if (!authScopeKey) {
      setDiagnosticsEnabled(false);
      setDiagnosticsRawEnabled(false);
      return () => {
        active = false;
      };
    }
    void fetchDiagnosticsCapabilities().then((capabilities) => {
      if (!active || authEpochRef.current !== epoch) return;
      setDiagnosticsEnabled(capabilities.enabled);
      setDiagnosticsRawEnabled(capabilities.raw_enabled);
    });
    return () => {
      active = false;
    };
  }, [authEpoch, authScopeKey]);

  useEffect(() => {
    refreshInbox();
    const t = setInterval(refreshInbox, 60_000);
    return () => clearInterval(t);
  }, [refreshInbox]);

  // Web-push click handoff: the Service Worker either postMessages an open-window
  // client ({type:'gritclaw-open'}) or cold-opens the Dashboard at ?gritclaw=open.
  // Either way: show the panel and re-poll the inbox so the proactive item lands.
  const openFromPush = useCallback(() => {
    setPanelOpen(true);
    refreshInbox();
  }, [setPanelOpen, refreshInbox]);

  // SW postMessage path: keep the listener registered without churning on panel
  // toggles. openFromPush's identity changes with refreshInbox (dep: panelOpen),
  // so a ref holds the latest callback and the effect itself runs once.
  const openFromPushRef = useRef(openFromPush);
  openFromPushRef.current = openFromPush;
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if ((e.data as { type?: string })?.type === 'gritclaw-open')
        openFromPushRef.current();
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);

  // Cold-open path: ?gritclaw=open must fire exactly once and then be stripped
  // from the URL. Without this, the effect re-running (refreshInbox identity
  // churns on every panel toggle) would re-detect the param and force the panel
  // back open, trapping the user with an un-closeable panel for the page load.
  const coldOpenHandledRef = useRef(false);
  useEffect(() => {
    if (coldOpenHandledRef.current) return;
    coldOpenHandledRef.current = true;
    if (
      new URLSearchParams(window.location.search).get('gritclaw') === 'open'
    ) {
      window.history.replaceState(
        {},
        '',
        window.location.pathname + window.location.hash
      );
      openFromPushRef.current();
    }
  }, []);

  // Cmd/Ctrl-K focuses the GritClaw bar — but only when the user isn't typing in
  // a field (don't steal the shortcut from inputs/textareas/contenteditables).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== 'k') return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      const typing =
        tag === 'input' || tag === 'textarea' || el?.isContentEditable === true;
      if (typing) return;
      e.preventDefault();
      focusBar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusBar]);

  const dismissInbox = useCallback(() => setInboxCount(0), []);

  const proactive = useMemo(
    () => deriveProactive(pendingItemsForCurrentEpoch[0]),
    [pendingItemsForCurrentEpoch]
  );

  const value = useMemo<GritClawContextValue>(
    () => ({
      messages: messagesForCurrentEpoch,
      isStreaming: isStreamingForCurrentEpoch,
      panelOpen,
      setPanelOpen,
      sendMessage,
      stopTurn,
      retryMessage,
      confirmAction,
      clearConversation,
      conversations: conversationsForCurrentEpoch,
      activeConversationId,
      conversationListLoading,
      conversationListError,
      historyLoading,
      historyLoadError,
      selectConversation,
      refreshConversations,
      renameConversation,
      setConversationPinned,
      deletingConversationIds: deletingConversationIdsForCurrentEpoch,
      clearingConversationHistory: clearingConversationHistoryForCurrentEpoch,
      conversationMutationPending,
      conversationEpoch: authEpoch,
      conversationRevision,
      removeConversation,
      clearConversationHistory,
      setScreenOverride,
      askGritClawAbout,
      detachScreenContext,
      currentOverride: overrideForCurrentEpoch,
      currentScreenContext,
      wakeIfNeeded,
      inboxCount: inboxCountForCurrentEpoch,
      dismissInbox,
      attachedContext: attachedContextForCurrentEpoch,
      attachContext,
      detachContext,
      clearContext,
      attachedFiles: attachedFilesForCurrentEpoch,
      attachFiles,
      detachFile,
      clearFiles,
      uploadingAttachments: uploadingAttachmentsForCurrentEpoch,
      barExpanded,
      setBarExpanded,
      requestFull,
      fullTransitioning,
      dismissedPins: dismissedPinsForCurrentEpoch,
      dismissPin,
      pendingItems: pendingItemsForCurrentEpoch,
      proactive,
      focusBarNonce,
      focusBar,
      diagnosticsEnabled: diagnosticsEnabledForCurrentEpoch,
      diagnosticsRawEnabled: diagnosticsRawEnabledForCurrentEpoch,
    }),
    [
      messagesForCurrentEpoch,
      isStreamingForCurrentEpoch,
      panelOpen,
      setPanelOpen,
      sendMessage,
      stopTurn,
      retryMessage,
      confirmAction,
      clearConversation,
      conversationsForCurrentEpoch,
      activeConversationId,
      conversationListLoading,
      conversationListError,
      historyLoading,
      historyLoadError,
      selectConversation,
      refreshConversations,
      renameConversation,
      setConversationPinned,
      deletingConversationIdsForCurrentEpoch,
      clearingConversationHistoryForCurrentEpoch,
      conversationMutationPending,
      authEpoch,
      conversationRevision,
      removeConversation,
      clearConversationHistory,
      setScreenOverride,
      askGritClawAbout,
      detachScreenContext,
      overrideForCurrentEpoch,
      currentScreenContext,
      wakeIfNeeded,
      inboxCountForCurrentEpoch,
      dismissInbox,
      attachedContextForCurrentEpoch,
      attachContext,
      detachContext,
      clearContext,
      attachedFilesForCurrentEpoch,
      attachFiles,
      detachFile,
      clearFiles,
      uploadingAttachmentsForCurrentEpoch,
      barExpanded,
      setBarExpanded,
      requestFull,
      fullTransitioning,
      dismissedPinsForCurrentEpoch,
      dismissPin,
      pendingItemsForCurrentEpoch,
      proactive,
      focusBarNonce,
      focusBar,
      diagnosticsEnabledForCurrentEpoch,
      diagnosticsRawEnabledForCurrentEpoch,
    ]
  );

  return (
    <GritClawCtx.Provider value={value}>
      {children}
      {/* App-wide toast surface for attachment upload / validation failures —
          every send path (panel, page, command bar) flows through this provider. */}
      <SnackbarComponent
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={snackbar.close}
        onRetry={snackbar.retry}
        // Distinct anchor (top-center) so this app-wide attachment toast never
        // collides with page-level snackbars (which use the default bottom-left).
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </GritClawCtx.Provider>
  );
};

export const useGritClaw = (): GritClawContextValue => {
  const ctx = useContext(GritClawCtx);
  if (!ctx)
    throw new Error('useGritClaw must be used within a GritClawProvider');
  return ctx;
};

/** Non-throwing variant: returns null when used outside a GritClawProvider.
 *  For shared hooks (e.g. device/shipment row menus) that may render in tests
 *  or contexts without the provider mounted. */
export const useGritClawOptional = (): GritClawContextValue | null =>
  useContext(GritClawCtx);
